import type {
  NutriScoreGrade,
  NutritionFacts,
  NutritionValues,
  Product,
  PurchaseHint,
  Store,
} from '@recipes/contracts';
import { NUTRIENT_KEYS } from '@recipes/contracts';
import { resolveHints, type LabelConflict } from '@recipes/domain';
import { Alert, Button, cn } from '@recipes/ui';
import { AlertTriangle } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { toastError } from '@/lib/errors';
import { HintMarkerBadge, NutriScoreBadge } from './badges';
import { patchProduct } from './catalog-queue';
import { HintList } from './hint-dialog';
import { ProductGallery, ProductThumbButton } from './product-photos';
import { currentFormatLocale } from '@/lib/i18n';

const UNIT: Record<(typeof NUTRIENT_KEYS)[number], string> = {
  energyKj: 'kJ',
  energyKcal: 'kcal',
  fat: 'g',
  saturatedFat: 'g',
  carbohydrates: 'g',
  sugars: 'g',
  fibre: 'g',
  protein: 'g',
  salt: 'g',
};

/** Values with their basis and source – never unexplained numbers. */
export function NutritionTable({
  values,
  facts,
  caption,
}: {
  values: NutritionValues;
  facts?: Pick<NutritionFacts, 'basis' | 'source' | 'attribution' | 'providerKey'> | null;
  caption: string;
}) {
  const { t } = useTranslation();
  const format = (value: string) =>
    new Intl.NumberFormat(currentFormatLocale(), { maximumFractionDigits: 2 }).format(
      Number(value),
    );
  const rows = NUTRIENT_KEYS.filter((key) => values[key] !== null);
  if (rows.length === 0) return null;
  return (
    <div>
      <table className="w-full text-sm" data-testid="nutrition-table">
        <caption className="mb-1 text-left text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          {caption}
        </caption>
        <tbody>
          {rows.map((key) => (
            <tr key={key} className="border-b last:border-0">
              <th
                scope="row"
                className={cn(
                  'py-1 text-left font-normal',
                  (key === 'saturatedFat' || key === 'sugars') && 'pl-4 text-muted-foreground',
                )}
              >
                {t(`catalog.nutrient.${key}`)}
              </th>
              <td className="py-1 text-right tabular-nums">
                {format(values[key]!)} {UNIT[key]}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {facts && (
        <p className="mt-1 text-xs text-muted-foreground">
          {t(`catalog.source.${facts.source}`)}
          {facts.attribution ? ` · ${facts.attribution}` : ''}
        </p>
      )}
    </div>
  );
}

/** Two taps: pick the printed letter, or record that none is printed (works offline). */
export function NutriScorePicker({
  product,
  onChanged,
}: {
  product: Product;
  onChanged?: (p: Product) => void;
}) {
  const { t } = useTranslation();
  const [busy, setBusy] = useState(false);
  const pick = async (patch: { nutriScore?: NutriScoreGrade | null; nutriScoreNone?: boolean }) => {
    setBusy(true);
    try {
      onChanged?.(await patchProduct(product, patch));
    } catch (error) {
      toastError(error);
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="rounded-md border border-dashed p-2" data-testid="nutri-score-picker">
      <p className="mb-1 text-xs text-muted-foreground">{t('catalog.nutriScore.ask')}</p>
      <div className="flex flex-wrap gap-1">
        {(['A', 'B', 'C', 'D', 'E'] as const).map((grade) => (
          <Button
            key={grade}
            type="button"
            size="sm"
            variant="outline"
            disabled={busy}
            aria-label={t('catalog.nutriScore.label', { grade })}
            onClick={() => void pick({ nutriScore: grade })}
            data-testid={`nutri-score-pick-${grade}`}
          >
            {grade}
          </Button>
        ))}
        <Button
          type="button"
          size="sm"
          variant="ghost"
          disabled={busy}
          onClick={() => void pick({ nutriScoreNone: true })}
        >
          {t('catalog.nutriScore.none')}
        </Button>
      </div>
    </div>
  );
}

/**
 * What the household thinks of a product, then what is in it (docs/finished/ingredient-catalog-spec.md
 * 5.1). Order matters in the store: verdict, hints for the current store, dietary conflicts,
 * then identity, Nutri-Score, nutrition and the ingredients list with their sources.
 */
export function ProductCard({
  product,
  hints,
  conflicts,
  store,
  pending,
  actions,
  onProductChanged,
}: {
  product: Product;
  hints: PurchaseHint[];
  conflicts: LabelConflict[];
  store: Store | null;
  pending?: boolean;
  actions?: ReactNode;
  onProductChanged?: (product: Product) => void;
}) {
  const { t } = useTranslation();
  const resolved = resolveHints(hints, {
    productId: product.id,
    ingredientId: product.ingredient?.id ?? null,
    storeId: store?.id ?? null,
    storeParentId: store?.parentStoreId ?? null,
  });
  const target = { productId: product.id, name: product.name };
  const pack = product.packageQuantity
    ? `${product.packageQuantity} ${product.packageUnitCode ?? product.packageUnitText ?? ''}`.trim()
    : product.packageUnitText;

  return (
    <article className="space-y-4" data-testid="product-card" aria-label={product.name}>
      <section aria-label={t('catalog.product.verdict')} className="space-y-2">
        <div className="flex flex-wrap items-center gap-2">
          {resolved.marker === 'NONE' ? (
            <span className="text-sm text-muted-foreground" data-testid="product-no-opinion">
              {t('catalog.product.noOpinion')}
            </span>
          ) : (
            <HintMarkerBadge marker={resolved.marker} className="text-sm" />
          )}
          {store && (
            <span className="text-xs text-muted-foreground">
              {t('catalog.shoppingAt.current', { store: store.name })}
            </span>
          )}
          {pending && (
            <span className="text-xs text-muted-foreground" data-testid="product-pending">
              {t('catalog.pendingSync')}
            </span>
          )}
        </div>
        <HintList hints={resolved.applicable} target={target} />
        {resolved.elsewhere.length > 0 && (
          <details className="text-sm">
            <summary className="cursor-pointer text-muted-foreground">
              {t('catalog.hint.elsewhere', { count: resolved.elsewhere.length })}
            </summary>
            <div className="mt-2">
              <HintList hints={resolved.elsewhere} target={target} />
            </div>
          </details>
        )}
      </section>

      {conflicts.length > 0 && (
        <Alert variant="warning" data-testid="product-conflicts">
          <AlertTriangle className="size-4" aria-hidden />
          <div>
            <p className="font-medium">
              {t('catalog.product.conflicts', { terms: conflicts.map((c) => c.term).join(', ') })}
            </p>
            <p className="text-xs">{t('catalog.product.conflictsVerify')}</p>
          </div>
        </Alert>
      )}

      <section className="flex gap-3">
        <ProductThumbButton
          product={{
            id: product.id,
            name: product.name,
            brand: product.brand,
            thumbnailUrl: product.photos[0]?.thumbnailUrl,
          }}
          className="size-20"
          placeholder
        />
        <div className="min-w-0 flex-1 space-y-1">
          <h2 className="text-lg font-semibold leading-tight">{product.name}</h2>
          <p className="text-sm text-muted-foreground">
            {[product.brand, pack].filter(Boolean).join(' · ') || t('catalog.product.noBrand')}
          </p>
          {product.ingredient && (
            <p className="text-xs text-muted-foreground">
              {t('catalog.product.ingredient', { name: product.ingredient.name })}
            </p>
          )}
          <div className="flex flex-wrap items-center gap-2 pt-1">
            {product.nutriScore && <NutriScoreBadge grade={product.nutriScore} />}
            {product.nutriScoreNone && (
              <span className="text-xs text-muted-foreground">
                {t('catalog.nutriScore.nonePrinted')}
              </span>
            )}
            {product.novaGroup !== null && (
              <span className="rounded border px-1.5 py-0.5 text-xs">
                {t('catalog.product.nova', { group: product.novaGroup })}
              </span>
            )}
          </div>
        </div>
      </section>

      {!product.nutriScore && !product.nutriScoreNone && (
        <NutriScorePicker product={product} onChanged={onProductChanged} />
      )}

      {actions && <div className="flex flex-wrap gap-2">{actions}</div>}

      <ProductGallery product={product} onChanged={onProductChanged} />

      {product.nutrition && (
        <NutritionTable
          values={product.nutrition}
          facts={product.nutrition}
          caption={t(`catalog.nutrition.${product.nutrition.basis}`)}
        />
      )}

      {product.ingredientsText && (
        <section>
          <h3 className="mb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            {t('catalog.product.ingredientsText')}
          </h3>
          <p className="text-sm">{product.ingredientsText}</p>
          {product.allergens.length > 0 && (
            <p className="mt-1 text-xs">
              <span className="font-medium">{t('catalog.product.allergens')}:</span>{' '}
              {product.allergens.join(', ')}{' '}
              <span className="text-muted-foreground">({t('catalog.product.fromLabel')})</span>
            </p>
          )}
        </section>
      )}

      {product.attribution && (
        <p className="text-xs text-muted-foreground" data-testid="product-attribution">
          {t('catalog.product.dataFrom', { source: product.attribution })}
        </p>
      )}
    </article>
  );
}
